// ============================================================================
// PATCH [2026-04-04] — Swipe-path variant desync fix
// ----------------------------------------------------------------------------
// Reported bug: customer on Facebook in-app webview reported that selecting
// a colour combination on The Wren produced a different combination in the
// cart. Video confirmed the slider swatches and main image were showing the
// selected colour, but the cart contained a different variant. Customer was
// a developer who flagged it as looking like an off-by-1 on the colour keys.
//
// Root cause: the CB override inside theme.js changeVariant() (search for
// "cb custom") reads .custom-options-container input[type="radio"]:checked
// synchronously to rebuild the correct variant key, because the Alpine
// @change handler on the native radios is server-rendered with a hardcoded
// value.variant.id that is only correct for single-option products. That
// synchronous read requires the custom radios to already reflect the new
// selection at the moment changeVariant runs.
//
// On direct label clicks this happened "for free" — the browser's native
// for=/id= behaviour checks the custom radio before Slick's focusOnSelect
// fires. But on swipes, arrow clicks, or any programmatic slickGoTo, the
// custom radio was never marked checked before triggerNativeOption() was
// called. The CB override then read the PREVIOUS selection and committed
// the wrong variant ID to the cart. Slider UI looked correct, cart was
// wrong. Swipe-heavy (mobile / in-app webview) users were disproportionately
// affected.
//
// The non-swatch (plain radio) afterChange handlers already had the
// equivalent $customInput.prop('checked', true) line. The bug was that all
// three swatch-path handlers (top / middle / bottom) were missing it.
//
// Fix: explicitly uncheck same-name siblings in .custom-options-container
// and check the target custom input BEFORE calling triggerNativeOption.
// Explicit uncheck rather than relying on radio-group exclusivity because
// form-owner semantics between custom radios and native radios are not
// guaranteed across all product templates.
//
// Verified on desktop Chrome against The Wren via console reproduction:
// before fix, slickGoTo(2) on the Body swatch carousel left both the
// custom container and native picker reporting the original Body colour
// while the slider UI showed the new one; after the pre-sync, both
// containers correctly reported the new colour and the section rendered
// the right variant form.
//
// See also: theme.js changeVariant() — a passive sanity-check logger has
// been added there to detect regressions of this class in production.
// ============================================================================

(function($) {
  
  function cbPenSlider() {
    if ($('.custom-slider-section').length) {
      const nativePickerRoot = '.product__block__variants .form__selectors';

      function escapeSelectorValue(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') {
          return window.CSS.escape(value);
        }
        return String(value).replace(/"/g, '\\"');
      }

      function getOptionNameFromInput($input) {
        const rawName = $input.attr('name') || '';
        const match = rawName.match(/^options\[(.*)\]$/);
        return match ? match[1] : '';
      }

      // Radios only. A popout-select option is rendered by
      // product-variant-options.liquid as
      //   <input type="hidden" name="options[Nib Type]" value="<selected>" data-popout-input>
      // so without the type filter this matched that hidden input whenever the
      // value asked for happened to be the one already selected. syncCustomOptionState
      // then read .prop('checked') on a hidden input — always false — which
      // short-circuited the $nativePopoutWrapper fallback that reads .val().
      // Net effect: the Pocket Pen's six Nib Type pills never showed a selected
      // state at all, for any nib, for the life of the page.
      function getNativeInput(optionName, optionValue) {
        const safeOptionName = escapeSelectorValue(optionName);
        const safeOptionValue = escapeSelectorValue(optionValue);

        return $(nativePickerRoot)
          .find(`input[type="radio"][name="options[${safeOptionName}]"][value="${safeOptionValue}"]`)
          .first();
      }

      function triggerNativeOption(optionName, optionValue) {
        const $nativeInput = getNativeInput(optionName, optionValue);

        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        if ($nativeInput.length) {
          const nativeInput = $nativeInput.get(0);
          if (!nativeInput) return;

          nativeInput.checked = true;
          nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
          nativeInput.dispatchEvent(new Event('change', { bubbles: true }));

          requestAnimationFrame(function() {
            window.scrollTo(scrollX, scrollY);
          });

          return;
        }

        const $popoutOption = getNativePopoutOption(optionName, optionValue);

        if ($popoutOption.length) {
          // Already on this value? Do nothing. This branch became reachable for
          // the CURRENT selection once getNativeInput stopped matching the
          // popout's hidden input above — before that it fell into the radio
          // branch and harmlessly re-checked a hidden input. Clicking the popout
          // link runs its @click.prevent changeVariant(), which morphs the whole
          // product section for a variant that is already selected and wipes
          // whatever Klaviyo injected into it a moment earlier. Slick fires
          // afterChange even for slickGoTo(currentSlide) — the same reason
          // topSyncingInitial exists below — so a plain click on the selected
          // pill lands here every time. The hidden input is authoritative:
          // theme.js's popout component writes it on every selection, and
          // syncCustomOptionState already trusts it.
          const $currentValue = $popoutOption
            .closest('[data-swapper-wrapper][data-split-select-wrapper]')
            .find('input[data-popout-input]')
            .first();

          if (($currentValue.val() || '').trim() === String(optionValue).trim()) return;

          const popoutOption = $popoutOption.get(0);

          popoutOption.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          }));

          requestAnimationFrame(function() {
            window.scrollTo(scrollX, scrollY);
          });

          return;
        }

        console.warn('Native option not found:', optionName, optionValue);
      }

      function getNativePopoutOption(optionName, optionValue) {
        const $wrapper = $(nativePickerRoot)
          .find('[data-swapper-wrapper][data-split-select-wrapper]')
          .filter(function() {
            return ($(this).attr('data-select-label') || '').trim() === optionName;
          })
          .first();

        if (!$wrapper.length) return $();

        return $wrapper
          .find('[data-popout-option]')
          .filter(function() {
            return ($(this).attr('data-value') || '').trim() === optionValue;
          })
          .first();
      }

      function syncCustomOptionState($customContainer) {
        $customContainer.find('input[type="radio"]').each(function() {
          const $customInput = $(this);
          const optionName = getOptionNameFromInput($customInput);
          const optionValue = $customInput.val();

          if (!optionName || !optionValue) return;

          const $nativeInput = getNativeInput(optionName, optionValue);
          const $nativePopoutWrapper = $(nativePickerRoot)
            .find('[data-swapper-wrapper][data-split-select-wrapper]')
            .filter(function() {
              return ($(this).attr('data-select-label') || '').trim() === optionName;
            })
            .first();

          let isChecked = false;
          let isDisabled = false;

          if ($nativeInput.length) {
            isChecked = $nativeInput.prop('checked');
            isDisabled = $nativeInput.prop('disabled');
          } else if ($nativePopoutWrapper.length) {
            const $hiddenInput = $nativePopoutWrapper.find('input[data-popout-input]').first();
            isChecked = (($hiddenInput.val() || '').trim() === String(optionValue).trim());
            isDisabled = false;
          } else {
            return;
          }

          $customInput.prop('checked', isChecked);
          $customInput.prop('disabled', isDisabled);

          if (isChecked) {
            $customInput.attr('checked', 'checked');
          } else {
            $customInput.removeAttr('checked');
          }

          if (isDisabled) {
            $customInput.attr('disabled', 'disabled');
          } else {
            $customInput.removeAttr('disabled');
          }

          const nativeClass = $nativeInput.attr('class') || '';
          const isSoldOut = nativeClass.indexOf('sold-out') !== -1;
          const isUnavailable = nativeClass.indexOf('unavailable') !== -1;

          // $customInput.toggleClass('sold-out', isSoldOut);
          $customInput.toggleClass('unavailable', isUnavailable);

          const $customWrapper = $customInput.closest('.radio__button, .swatch__button');
          // $customWrapper.toggleClass('sold-out', isSoldOut);
          $customWrapper.toggleClass('unavailable', isUnavailable);

          if (isChecked) {
            $customContainer
              .parents('fieldset')
              .find('[data-option-value]')
              .html(optionValue);
          }
        });
      }

      function syncAllCustomOptions() {
        if (top_slider_option_alias !== false) {
          syncCustomOptionState($('#custom-' + top_slider_option_alias + '-thumbnails'));
        }
        if (middle_slider_option_alias !== false) {
          syncCustomOptionState($('#custom-' + middle_slider_option_alias + '-thumbnails'));
        }
        if (bottom_slider_option_alias !== false) {
          syncCustomOptionState($('#custom-' + bottom_slider_option_alias + '-thumbnails'));
        }
      }

      let top_slider_option_alias = false;
      let middle_slider_option_alias = false;
      let bottom_slider_option_alias = false;

      let top_slider_json_el = $('#top_option_slider_json');
      let middle_slider_json_el = $('#middle_option_slider_json');
      let bottom_slider_json_el = $('#bottom_option_slider_json');
      if (top_slider_json_el.length > 0) {
        let top_slider_json = JSON.parse($(top_slider_json_el).text());
        for (let idx = 0; idx < top_slider_json.length; idx++) {
          top_slider_option_alias = top_slider_json[idx]['option_name_handleize'];
        }
      }
      if (middle_slider_json_el.length > 0) {
        let middle_slider_json = JSON.parse($(middle_slider_json_el).text());
        for (let idx = 0; idx < middle_slider_json.length; idx++) {
          middle_slider_option_alias = middle_slider_json[idx]['option_name_handleize'];
        }
      }
      if (bottom_slider_json_el.length > 0) {
        let bottom_slider_json = JSON.parse($(bottom_slider_json_el).text());
        for (let idx = 0; idx < bottom_slider_json.length; idx++) {
          bottom_slider_option_alias = bottom_slider_json[idx]['option_name_handleize'];
        }
      }

      const arrowPrev = "<button class='flickity-button flickity-prev-next-button previous lid-previous' type='button' aria-label='Previous'><svg class='flickity-button-icon' viewBox='0 0 100 100'><title>Previous</title><path d='M 10, 50 L 60, 100 L 67.5, 92.5 L 25, 50 L 67.5, 7.5 L 60, 0 Z' class='arrow' ></path></svg></button>",
            arrowNext = "<button class='flickity-button flickity-prev-next-button next lid-next' type='button' aria-label='Next'><svg class='flickity-button-icon' viewBox='0 0 100 100'><title>Next</title><path d='M 10, 50 L 60, 100 L 67.5, 92.5 L 25, 50 L 67.5, 7.5 L 60, 0 Z' class='arrow' transform='translate(100, 100) rotate(180)'></path></svg></button>";

      // TOP Slider
      if (top_slider_option_alias !== false) {
        const topSliderTarget = $('#main-custom-' + top_slider_option_alias).find('.main-carousel');
        const topSwatchesTarget = $('#custom-' + top_slider_option_alias + '-thumbnails');

        const swatchesCount = topSwatchesTarget.find('radio-swatch').length;
        let swatchesToShow = swatchesCount <= 5 ? 3 : 5;

        // Set while the carousel is being jumped to the already-selected
        // slide on load/init (see initialTopSelectedIndex / initialTopButtonIndex
        // below), so the resulting 'afterChange' knows this wasn't a real user
        // pick and skips re-triggering the native picker. Without this, jumping
        // to a non-zero starting slide fires a real Slick 'afterChange' for a
        // variant that was already selected server-side, which re-dispatches a
        // native 'change' event and runs changeVariant() again for no reason —
        // morphing the product section (and wiping anything a third-party
        // script, e.g. Klaviyo's back-in-stock button, injected into it a
        // moment earlier) on every single page load.
        let topSyncingInitial = false;

        if ($(topSwatchesTarget).hasClass('cb-swatch-opt')) {
          
          let swatchesTopOptions = {
            slidesToShow: swatchesToShow,
            infinite: true,
            dots: false,
            asNavFor: topSliderTarget,
            centerMode: true,
            focusOnSelect: true,
            arrows:false,
            responsive: [
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: swatchesToShow
                }
              }
            ]
          };

          topSliderTarget.on('init', function(event, slick, currentSlide) {
            let currentSlideTop = $(slick.$slides.get(currentSlide));
            currentSlideTop.parents('.custom-slider-section').find('.loader').hide();
            currentSlideTop.parents('.custom-slider-section').find('.preloader').fadeOut();

            if (initialTopSelectedIndex >= 0) {
              setTimeout(function() {
                topSyncingInitial = true;
                topSliderTarget.slick('slickGoTo', initialTopSelectedIndex, true);
                topSwatchesTarget.slick('slickGoTo', initialTopSelectedIndex, true);
                topSliderTarget.slick('setPosition');
                topSwatchesTarget.slick('setPosition');
                topSyncingInitial = false;
              }, 0);
            }
          });

          let initialTopSelectedIndex = topSwatchesTarget.find('input[type="radio"]').index(
            topSwatchesTarget.find('input[type="radio"]:checked').first()
          );
          topSwatchesTarget.slick(swatchesTopOptions);

          let times = 2;

          topSwatchesTarget.on('afterChange', function(event, slick, currentSlide, nextSlide) {
            let currentSlideTop = $(slick.$slides.get(currentSlide));

            if (!currentSlideTop.hasClass('slick-cloned') && !topSyncingInitial) {
              let $customInput = currentSlideTop.find('input').first();
              let optionName = getOptionNameFromInput($customInput);
              let optionValue = $customInput.val();

              if (optionName && optionValue) {
                const nameAttr = 'options[' + optionName + ']';
                $('.custom-options-container input[type="radio"]').filter(function() {
                  return this.name === nameAttr;
                }).prop('checked', false);
                $customInput.prop('checked', true);

                triggerNativeOption(optionName, optionValue);
                currentSlideTop.parents('fieldset').find('[data-option-value]').html(optionValue);
              }
            }

            // if (times === 0) {
              topSliderTarget.slick('slickPause');
              topSwatchesTarget.slick('slickPause');
            // }
            --times;
          });
          topSliderTarget.on('init', function(event, slick, currentSlide, nextSlide){
            let currentSlideTop = $(slick.$slides.get(currentSlide)); 
            currentSlideTop.parents('.custom-slider-section').find('.loader').hide();
            currentSlideTop.parents('.custom-slider-section').find('.preloader').fadeOut();
          });

          $sliderTop = topSliderTarget.slick({
            slidesToShow: 1,
            infinite: true,
            dots: false,
            centerMode: true,
            focusOnSelect: true,
            prevArrow: arrowPrev,
            nextArrow: arrowNext,
            asNavFor:topSwatchesTarget,
            autoplay: false,
            autoplaySpeed: 4000
          });
        } else {
          $sliderTop = topSliderTarget.slick({
            slidesToShow: 1,
            infinite: true,
            dots: false,
            centerMode: true,
            focusOnSelect: true,
            prevArrow: arrowPrev,
            nextArrow: arrowNext,
            autoplay: false,
            autoplaySpeed: 4000
          });

          let times = 2;

          topSliderTarget.on('afterChange', function(event, slick, currentSlide, nextSlide) {
            let currentSlideTop = $(slick.$slides.get(currentSlide));
            if (!topSyncingInitial) {
              currentSlideTop.parents('.product-page').find('#custom-' + top_slider_option_alias + '-thumbnails .radio__button').each(function(i,k) {
                if (i == currentSlide) {
                  let $customInput = $(this).find('input').first();
                  let optionName = getOptionNameFromInput($customInput);
                  let optionValue = $customInput.val();

                  $customInput.prop('checked', true);

                  if (optionName && optionValue) {
                    triggerNativeOption(optionName, optionValue);
                  }
                }

              });
            }

            // if (times === 0) {
              topSliderTarget.slick('slickPause');
            // }
            --times;
          });
          $('#custom-' + top_slider_option_alias + '-thumbnails .radio__button').each(function(i,k) {
            $(this).find('label').on('click', function() {
              topSliderTarget.slick('slickGoTo', i);
            });
          });
          
          topSliderTarget.on('init', function(event, slick, currentSlide, nextSlide){
            let currentSlideTop = $(slick.$slides.get(currentSlide));
            currentSlideTop.parents('.custom-slider-section').find('.loader').hide();
            currentSlideTop.parents('.custom-slider-section').find('.preloader').fadeOut();
          });

          let initialTopButtonIndex = $('#custom-' + top_slider_option_alias + '-thumbnails input[type="radio"]').index(
            $('#custom-' + top_slider_option_alias + '-thumbnails input[type="radio"]:checked').first()
          );

          if (initialTopButtonIndex >= 0) {
            setTimeout(function() {
              topSyncingInitial = true;
              topSliderTarget.slick('slickGoTo', initialTopButtonIndex, true);
              topSliderTarget.slick('setPosition');
              topSyncingInitial = false;
            }, 0);
          }
        }

      }

      // MIDDLE Slider
      if (middle_slider_option_alias !== false) {
        const middleSliderTarget = $('#main-custom-' + middle_slider_option_alias).find('.main-carousel');
        const middleSwatchesTarget = $('#custom-' + middle_slider_option_alias + '-thumbnails');
      
        const swatchesCount = middleSwatchesTarget.find('radio-swatch').length;
        const swatchesToShow = swatchesCount <= 5 ? 3 : 5;

        // See topSyncingInitial above for why this exists.
        let middleSyncingInitial = false;

        if ($(middleSwatchesTarget).hasClass('cb-swatch-opt')) {
          let swatchesMiddleOptions = {
            slidesToShow: swatchesToShow,
            infinite: true,
            dots: false,
            asNavFor: middleSliderTarget,
            centerMode: true,
            focusOnSelect: true,
            arrows: false,
            responsive: [
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: swatchesToShow
                }
              }
            ]
          };

          middleSliderTarget.on('init', function(event, slick, currentSlide) {
            let currentSlideMiddle = $(slick.$slides.get(currentSlide));
            currentSlideMiddle.parents('.custom-slider-section').find('.loader').hide();
            currentSlideMiddle.parents('.custom-slider-section').find('.preloader').fadeOut();

            if (initialMiddleSelectedIndex >= 0) {
              setTimeout(function() {
                middleSyncingInitial = true;
                middleSliderTarget.slick('slickGoTo', initialMiddleSelectedIndex, true);
                middleSwatchesTarget.slick('slickGoTo', initialMiddleSelectedIndex, true);
                middleSliderTarget.slick('setPosition');
                middleSwatchesTarget.slick('setPosition');
                middleSyncingInitial = false;
              }, 0);
            }
          });

          let initialMiddleSelectedIndex = middleSwatchesTarget.find('input[type="radio"]').index(
            middleSwatchesTarget.find('input[type="radio"]:checked').first()
          );
          middleSwatchesTarget.slick(swatchesMiddleOptions);

          let times = 2;

          middleSwatchesTarget.on('afterChange', function(event, slick, currentSlide, nextSlide) {
            let currentSlideMiddle = $(slick.$slides.get(currentSlide));

            if (!currentSlideMiddle.hasClass('slick-cloned') && !middleSyncingInitial) {
              let $customInput = currentSlideMiddle.find('input').first();
              let optionName = getOptionNameFromInput($customInput);
              let optionValue = $customInput.val();

              if (optionName && optionValue) {
                const nameAttr = 'options[' + optionName + ']';
                $('.custom-options-container input[type="radio"]').filter(function() {
                  return this.name === nameAttr;
                }).prop('checked', false);
                $customInput.prop('checked', true);

                triggerNativeOption(optionName, optionValue);
                currentSlideMiddle.parents('fieldset').find('[data-option-value]').html(optionValue);
              }
            }

            // if (times === 0) {
              middleSliderTarget.slick('slickPause');
              middleSwatchesTarget.slick('slickPause');
            // }
            --times;
          });

          middleSliderTarget.on('init', function(event, slick, currentSlide, nextSlide) {
            let currentSlideMiddle = $(slick.$slides.get(currentSlide));
            currentSlideMiddle.parents('.custom-slider-section').find('.loader').hide();
            currentSlideMiddle.parents('.custom-slider-section').find('.preloader').fadeOut();
          });

          $sliderMiddle = middleSliderTarget.slick({
            slidesToShow: 1,
            infinite: true,
            dots: false,
            centerMode: true,
            focusOnSelect: true,
            prevArrow: arrowPrev,
            nextArrow: arrowNext,
            asNavFor: middleSwatchesTarget,
            autoplay: false,
            autoplaySpeed: 2000
          });
        } else {
          $sliderMiddle = middleSliderTarget.slick({
            slidesToShow: 1,
            infinite: true,
            dots: false,
            centerMode: true,
            focusOnSelect: true,
            prevArrow: arrowPrev,
            nextArrow: arrowNext,
            autoplay: false,
            autoplaySpeed: 2000
          });
      
          let times = 2;
      
          middleSliderTarget.on('afterChange', function(event, slick, currentSlide, nextSlide) {
            let currentSlideMiddle = $(slick.$slides.get(currentSlide));

            if (!middleSyncingInitial) {
              currentSlideMiddle.parents('.product-page').find('#custom-' + middle_slider_option_alias + '-thumbnails .radio__button').each(function(i,k) {
                if (i == currentSlide) {
                  let $customInput = $(this).find('input').first();
                  let optionName = getOptionNameFromInput($customInput);
                  let optionValue = $customInput.val();

                  $customInput.prop('checked', true);

                  if (optionName && optionValue) {
                    triggerNativeOption(optionName, optionValue);
                  }
                }

              });
            }
      
            // if (times === 0) {
              middleSliderTarget.slick('slickPause');
            // }
            --times;
          });
      
          $('#custom-' + middle_slider_option_alias + '-thumbnails .radio__button').each(function(i) {
            $(this).find('label').on('click', function() {
              middleSliderTarget.slick('slickGoTo', i);
            });
          });
      
          middleSliderTarget.on('init', function(event, slick, currentSlide, nextSlide) {
            let currentSlideMiddle = $(slick.$slides.get(currentSlide));
            currentSlideMiddle.parents('.custom-slider-section').find('.loader').hide();
            currentSlideMiddle.parents('.custom-slider-section').find('.preloader').fadeOut();
          });

          let initialMiddleButtonIndex = $('#custom-' + middle_slider_option_alias + '-thumbnails input[type="radio"]').index(
            $('#custom-' + middle_slider_option_alias + '-thumbnails input[type="radio"]:checked').first()
          );

          if (initialMiddleButtonIndex >= 0) {
            setTimeout(function() {
              middleSyncingInitial = true;
              middleSliderTarget.slick('slickGoTo', initialMiddleButtonIndex, true);
              middleSliderTarget.slick('setPosition');
              middleSyncingInitial = false;
            }, 0);
          }
        }
      }

      // BOTTOM Slider
      if (bottom_slider_option_alias !== false) {
        const bottomSliderTarget = $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel');
        const bottomSwatchesTarget = $('#custom-' + bottom_slider_option_alias + '-thumbnails');

        const swatchesCount = bottomSwatchesTarget.find('radio-swatch').length;
        const swatchesToShow = swatchesCount <= 5 ? 3 : 5;

        // See topSyncingInitial above for why this exists.
        let bottomSyncingInitial = false;

        if ($(bottomSwatchesTarget).hasClass('cb-swatch-opt')) {
          let swatchesBottomOptions = {
            slidesToShow: swatchesToShow,
            infinite: true,
            dots: false,
            asNavFor: bottomSliderTarget,
            centerMode: true,
            focusOnSelect: true,
            arrows:false,
            responsive: [
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: swatchesToShow
                }
              }
            ]
          };

          bottomSliderTarget.on('init', function(event, slick, currentSlide) {
            let currentSlideBottom = $(slick.$slides.get(currentSlide));
            currentSlideBottom.parents('.custom-slider-section').find('.loader').hide();
            currentSlideBottom.parents('.custom-slider-section').find('.preloader').fadeOut();

            if (initialBottomSelectedIndex >= 0) {
              setTimeout(function() {
                bottomSyncingInitial = true;
                bottomSliderTarget.slick('slickGoTo', initialBottomSelectedIndex, true);
                bottomSwatchesTarget.slick('slickGoTo', initialBottomSelectedIndex, true);
                bottomSliderTarget.slick('setPosition');
                bottomSwatchesTarget.slick('setPosition');
                bottomSyncingInitial = false;
              }, 0);
            }
          });

          let initialBottomSelectedIndex = bottomSwatchesTarget.find('input[type="radio"]').index(
            bottomSwatchesTarget.find('input[type="radio"]:checked').first()
          );
          bottomSwatchesTarget.slick(swatchesBottomOptions);

          let times = 2;

          bottomSwatchesTarget.on('afterChange', function(event, slick, currentSlide, nextSlide) {
            let currentSlideBottom = $(slick.$slides.get(currentSlide));

            if (!currentSlideBottom.hasClass('slick-cloned') && !bottomSyncingInitial) {
              let $customInput = currentSlideBottom.find('input').first();
              let optionName = getOptionNameFromInput($customInput);
              let optionValue = $customInput.val();

              if (optionName && optionValue) {
                const nameAttr = 'options[' + optionName + ']';
                $('.custom-options-container input[type="radio"]').filter(function() {
                  return this.name === nameAttr;
                }).prop('checked', false);
                $customInput.prop('checked', true);

                triggerNativeOption(optionName, optionValue);
                currentSlideBottom.parents('fieldset').find('[data-option-value]').html(optionValue);
              }
            }

            // if (times === 0) {
              bottomSliderTarget.slick('slickPause');
              bottomSwatchesTarget.slick('slickPause');
            // }
            --times;
          });
          bottomSliderTarget.on('init', function(event, slick, currentSlide, nextSlide){
            let currentSlideBottom = $(slick.$slides.get(currentSlide)); 
            currentSlideBottom.parents('.custom-slider-section').find('.loader').hide();
            currentSlideBottom.parents('.custom-slider-section').find('.preloader').fadeOut();
          });

          $sliderBottom = bottomSliderTarget.slick({
            slidesToShow: 1,
            infinite: true,
            dots: false,
            centerMode: true,
            focusOnSelect: true,
            prevArrow: arrowPrev,
            nextArrow: arrowNext,
            asNavFor:bottomSwatchesTarget,
            autoplay: false,
            autoplaySpeed: 3000
          });
        } else {
          $sliderBottom = bottomSliderTarget.slick({
            slidesToShow: 1,
            infinite: true,
            dots: false,
            centerMode: true,
            focusOnSelect: true,
            prevArrow: arrowPrev,
            nextArrow: arrowNext,
            autoplay: false,
            autoplaySpeed: 3000
          });

          let times = 2;

          bottomSliderTarget.on('afterChange', function(event, slick, currentSlide, nextSlide) {
            let currentSlideBottom = $(slick.$slides.get(currentSlide));

            if (!bottomSyncingInitial) {
              currentSlideBottom.parents('.product-page').find('#custom-' + bottom_slider_option_alias + '-thumbnails .radio__button').each(function(i,k) {
                if (i == currentSlide) {
                  let $customInput = $(this).find('input').first();
                  let optionName = getOptionNameFromInput($customInput);
                  let optionValue = $customInput.val();

                  $customInput.prop('checked', true);

                  if (optionName && optionValue) {
                    triggerNativeOption(optionName, optionValue);
                  }
                }

              });
            }

            // if (times === 0) {
              bottomSliderTarget.slick('slickPause');
            // }
            --times;
          });

          $('#custom-' + bottom_slider_option_alias + '-thumbnails .radio__button').each(function(i,k) {
            $(this).find('label').on('click', function() {
              bottomSliderTarget.slick('slickGoTo', i);
            });
          });

          bottomSliderTarget.on('init', function(event, slick, currentSlide, nextSlide){
            let currentSlideBottom = $(slick.$slides.get(currentSlide));
            currentSlideBottom.parents('.custom-slider-section').find('.loader').hide();
            currentSlideBottom.parents('.custom-slider-section').find('.preloader').fadeOut();
          });

          let initialBottomButtonIndex = $('#custom-' + bottom_slider_option_alias + '-thumbnails input[type="radio"]').index(
            $('#custom-' + bottom_slider_option_alias + '-thumbnails input[type="radio"]:checked').first()
          );

          if (initialBottomButtonIndex >= 0) {
            setTimeout(function() {
              bottomSyncingInitial = true;
              bottomSliderTarget.slick('slickGoTo', initialBottomButtonIndex, true);
              bottomSliderTarget.slick('setPosition');
              bottomSyncingInitial = false;
            }, 0);
          }
        }

      }

      $('.custom-slider-container .flickity-prev-next-button').on('click', function() {
        if (top_slider_option_alias !== false) {
          $('#main-custom-' + top_slider_option_alias).find('.main-carousel').slick('slickPause');
        }
        if (middle_slider_option_alias !== false) {
          $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').slick('slickPause');
        }
        if (bottom_slider_option_alias !== false) {
          $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').slick('slickPause');
        }
      });

      $('label.cb-slider-option-label').on('click', function() {
        if (top_slider_option_alias !== false) {
          $('#main-custom-' + top_slider_option_alias).find('.main-carousel').slick('slickPause');
        }
        if (middle_slider_option_alias !== false) {
          $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').slick('slickPause');
        }
        if (bottom_slider_option_alias !== false) {
          $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').slick('slickPause');
        }
      });

      // $('.cb-slider-option-label').on('click', function() {
      // // $('.radio__inputs.cb-swatch-opt').on('click', function() {
      //   if (top_slider_option_alias !== false) {
      //     $('#main-custom-' + top_slider_option_alias).find('.main-carousel').slick('slickPause');
      //   }
      //   if (middle_slider_option_alias !== false) {
      //     $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').slick('slickPause');
      //   }
      //   if (bottom_slider_option_alias !== false) {
      //     $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').slick('slickPause');
      //   }
      // });

      if (top_slider_option_alias !== false) {
        $('#main-custom-' + top_slider_option_alias).find('.main-carousel').on('swipe', function(event, slick, direction) {
          $('#main-custom-' + top_slider_option_alias).find('.main-carousel').slick('slickPause');
          if (middle_slider_option_alias !== false) {
            $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').slick('slickPause');
          }
          if (bottom_slider_option_alias !== false) {
            $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').slick('slickPause');
          }
        });
      }

      if (middle_slider_option_alias !== false) {
        $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').on('swipe', function(event, slick, direction) {
          if (top_slider_option_alias !== false) {
            $('#main-custom-' + top_slider_option_alias).find('.main-carousel').slick('slickPause');
          }
          $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').slick('slickPause');
          if (bottom_slider_option_alias !== false) {
            $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').slick('slickPause');
          }
        });
      }

      if (bottom_slider_option_alias !== false) {
        $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').on('swipe', function(event, slick, direction) {
          if (top_slider_option_alias !== false) {
            $('#main-custom-' + top_slider_option_alias).find('.main-carousel').slick('slickPause');
          }
          if (middle_slider_option_alias !== false) {
            $('#main-custom-' + middle_slider_option_alias).find('.main-carousel').slick('slickPause');
          }
          $('#main-custom-' + bottom_slider_option_alias).find('.main-carousel').slick('slickPause');
        });
      }

      syncAllCustomOptions();
      setTimeout(function() {
        syncAllCustomOptions();
      }, 50);

      $(document).on('change', nativePickerRoot + ' input[type="radio"]', function() {
        setTimeout(function() {
          syncAllCustomOptions();
        }, 0);
      });

      const nativePickerNode = document.querySelector('.product__block__variants .form__selectors');
      if (nativePickerNode && !nativePickerNode._cbObserverAttached) {
        const observer = new MutationObserver(function() {
          syncAllCustomOptions();
        });

        observer.observe(nativePickerNode, {
          childList: true,
          subtree: true,
          attributes: true
        });

        nativePickerNode._cbObserverAttached = true;
      }

      if (window.matchMedia("(min-width: 768px)").matches && $('.position--gallery').length) {
        $('.custom-options-container').insertBefore('.product__main__content .product__block__buttons');
      } 
      if (window.matchMedia("(max-width: 767px)").matches) {
        // $(".product__block__title, div[id^='shopify-block-judge_me_reviews_preview_badge_']").wrapAll("<div class='title-review'>");
        // $('.title-review').prependTo('.product-page');
        // $('.cb-gallery-js').insertAfter('.product__main__content .product__block__buttons');
      }
  
    } //endif
  } //end slider function

  
  $(document).ready(function() {
    cbPenSlider();
    if ($('.custom-slider-section').length) {
      // let container = document.querySelector('.custom-slider-section');
      // const productRoot = container.closest('[x-data]');
      // const productData = Alpine.$data(productRoot);

      // container.addEventListener('click', (e) => {
      //   const swatch = e.target.closest('[data-swatch]');
      //   if (!swatch) return;
      //   if (swatch.closest('.slick-cloned')) return;

      //   productData.$sectionApi.navigate(swatch.dataset.variantUrl, 'replace');
      // });
    }
    
  });
})(jQuery);